import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../state/store';
import { isTokenExpired } from '../utils/tokenUtils';

class ApiService {

    private static readonly instances: Map<string, ApiService> = new Map();
    private static isGloballyRefreshing: boolean = false;
    private readonly axiosInstance: AxiosInstance;
    private isRefreshing: boolean = false;
    private readonly baseURL: string;

    private constructor(baseURL: string) {
        this.baseURL = baseURL;
        this.axiosInstance = axios.create({
            baseURL: baseURL
        });
        this.setupInterceptors();
    }

    public static getInstance(baseURL: string): ApiService {
        if (!ApiService.instances.has(baseURL)) {
            ApiService.instances.set(baseURL, new ApiService(baseURL));
        }
        return ApiService.instances.get(baseURL)!;
    }

    private static globalTokenRefreshCallback?: () => Promise<void>;

    public static setGlobalTokenRefreshCallback(callback: () => Promise<void>) {
        ApiService.globalTokenRefreshCallback = callback;
    }

    private setupInterceptors() {
        this.axiosInstance.interceptors.request.use(
            async (config) => {
                await this.handleTokenRefresh();
                
                // Update the Authorization header with the current token from the store
                // This ensures requests use the latest token (which may have been refreshed),
                // not the stale token passed when the call was initiated.
                // Only update if an Authorization header was already set (to avoid adding it to
                // requests that use different auth methods like X-Eli-Pat).
                const currentToken = store.getState().appSlice.accessToken;
                if (currentToken && config.headers?.['Authorization']) {
                    config.headers['Authorization'] = `Bearer ${currentToken}`;
                }
                
                return config;
            },
            (error) => {
                return Promise.reject(error instanceof Error ? error : new Error(String(error)));
            }
        );
    }

    private async handleTokenRefresh(): Promise<void> {
        if (this.isRefreshing || !this.shouldRefreshToken()) {
            return;
        }

        // Check if any instance is already globally refreshing
        if (ApiService.isGloballyRefreshing) {
            return;
        }

        if (!ApiService.globalTokenRefreshCallback) {
            console.warn(`Token is expired but no refresh callback is set for ApiService instance with baseURL: ${this.baseURL}`);
            return;
        }

        this.isRefreshing = true;
        ApiService.isGloballyRefreshing = true;
        
        try {
            await ApiService.globalTokenRefreshCallback();
        } catch (error) {
            console.error(`Token refresh failed for ${this.baseURL}:`, error);
        } finally {
            this.isRefreshing = false;
            ApiService.isGloballyRefreshing = false;
        }
    }

    private shouldRefreshToken(): boolean {
        const expirationDateTimeUTC = store.getState().appSlice.expirationDateTimeUTC;
        const accessToken = store.getState().appSlice.accessToken;
        
        // Don't refresh if we don't have a token yet (initial load)
        if (!accessToken || !expirationDateTimeUTC) {
            return false;
        }
        
        return isTokenExpired(accessToken, expirationDateTimeUTC);
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.get<T>(url, config);
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.post<T>(url, data, config)
    }

    public async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.put<T>(url, data, config);
    }
}

export default ApiService;